import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { srcSetFor } from "../lib/images";
import { SHOP_GROUPS, type ShopLink as ShopLinkT } from "../lib/schedule";

// The proposal, told as the site's most important Q&A. Captions read as one
// sentence across the three frames.
const PROPOSAL_SLIDES = [
  { src: "/proposal/proposal-1.webp", letter: "Q" },
  { src: "/proposal/proposal-3.webp", letter: "A" },
];

export function FAQ() {
  const { hash } = useLocation();

  // React Router doesn't scroll to hash targets on its own — do it here once the
  // questions are mounted (and again whenever the hash changes), so a link like
  // /faq#indian-clothes lands on the right question.
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <div className="mx-auto max-w-2xl">
      {/* Proposal photos, flush under the nav (cancels the page's top padding),
          side by side and centered at a fixed height. */}
      <div className="relative left-1/2 -mt-12 w-screen -translate-x-1/2">
        <div className="mx-auto flex max-w-2xl justify-center gap-3 px-6 sm:gap-4">
          {PROPOSAL_SLIDES.map((s) => (
            <img
              key={s.src}
              src={s.src}
              srcSet={srcSetFor(s.src, [500], 900)}
              sizes="(min-width: 640px) 245px, 170px"
              width={900}
              height={1188}
              alt="Karishma and Ben's proposal"
              decoding="async"
              className="h-56 w-auto sm:h-72 md:h-80"
            />
          ))}
        </div>
      </div>

      <header className="mt-8 sm:mt-12">
        {/* Desktop shows the full title. Mobile shows "Q & A" with each letter
            anchored under its photo: the boxes match the fixed photo width
            (h-56 → ~170px) and gap, so Q/A sit on the photo centers and the
            ampersand lands on the split. */}
        <h1 className="font-serif text-pine" aria-label="Questions & Answers">
          <span className="hidden text-center text-6xl sm:block">
            Questions &amp; Answers
          </span>
          <span
            aria-hidden="true"
            className="relative flex justify-center gap-3 text-5xl sm:hidden"
          >
            {PROPOSAL_SLIDES.map((s) => (
              <span key={s.src} className="w-[170px] text-center">
                {s.letter}
              </span>
            ))}
            <span className="absolute inset-0 flex items-center justify-center">
              &amp;
            </span>
          </span>
        </h1>
      </header>

      <div className="mt-6 sm:mt-10">
        <FaqItem q="When should I RSVP by?">
          <p>
            Please let us know by October 30th, 2026 if you are able to attend
            and note any dietary restrictions. Formal invitations will be sent a
            few months prior to the wedding to everyone who indicates that they
            are able to join us.
          </p>
        </FaqItem>

        <FaqItem q="Can I bring a plus-one or my children?">
          <p>
            Due to limited venue capacity, we are unfortunately only able to
            accommodate those listed on the invitation. Thank you for your
            understanding!
          </p>
        </FaqItem>

        <FaqItem q="Do you have any hotel recommendations?">
          <p>
            Yes! We have secured a special rate at the Hilton Richmond Hotel
            &amp; Spa/Short Pump, approximately 30 minutes from the Estate at
            River Run. See the{" "}
            <InlineLink to="/travel">Travel page</InlineLink> for more
            information on our room block.
          </p>
        </FaqItem>

        <FaqItem q="Is there transportation to and from the hotel to the reception?">
          <p>
            Shuttle service will be available to and from our room block hotel
            for events on Friday and Saturday evening &mdash; more details on
            shuttle schedules and logistics will be shared closer to the
            wedding.
          </p>
        </FaqItem>

        <FaqItem
          id="indian-clothes"
          q="I want to wear Indian clothes! Where do I begin?"
        >
          <p>
            Below are some links to retailers that ship to the US we recommend
            if you&rsquo;re looking for an outfit:
          </p>

          <div className="mt-6 grid gap-x-10 gap-y-7 sm:grid-cols-2">
            {SHOP_GROUPS.map((group) => (
              <div key={group.heading}>
                <p className="font-sans text-base font-medium text-pine">
                  {group.heading}
                </p>
                {group.rent && (
                  <ShopSubList label="To rent" links={group.rent} />
                )}
                <ShopSubList
                  label={group.rent ? "To buy" : undefined}
                  links={group.buy}
                />
              </div>
            ))}
          </div>

          <p className="mt-6">
            Please refer to the <InlineLink to="/schedule">Schedule</InlineLink>{" "}
            tab for a more detailed breakdown of dress code by event, and feel
            free to reach out to Karishma or the Srikanth family with any
            questions :)
          </p>
        </FaqItem>
      </div>
    </div>
  );
}

/**
 * One question, separated from the last by a hairline rule and a single
 * sage-moss diamond — the site's print vocabulary, shared with the Travel page.
 */
function FaqItem({
  q,
  id,
  children,
}: {
  q: string;
  id?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 mt-8 sm:mt-12">
      <div aria-hidden="true" className="flex items-center gap-3">
        <span className="h-px flex-1 bg-pine/15" />
        <span className="inline-block h-1.5 w-1.5 rotate-45 bg-sage-moss" />
        <span className="h-px flex-1 bg-pine/15" />
      </div>
      <h2 className="mt-8 font-serif text-3xl text-pine">{q}</h2>
      <div className="mt-4 space-y-4 font-sans text-lg font-normal leading-relaxed tracking-[0.02em] text-pine">
        {children}
      </div>
    </section>
  );
}

/** A "To rent" / "To buy" sub-list under a garment heading. The label is
 *  omitted when a group has only one list (nothing to distinguish). */
function ShopSubList({
  label,
  links,
}: {
  label?: string;
  links: ShopLinkT[];
}) {
  return (
    <div className="mt-2.5">
      {label && <p className="font-sans text-base text-pine/55">{label}</p>}
      <ul className={`${label ? "mt-1" : ""} space-y-1`}>
        {links.map((link) => (
          <li key={link.label + link.href}>
            <ShopLink {...link} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Outbound shop/rental link in the small-caps underline style. */
function ShopLink({ label, href }: { label: string; href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-sans text-base uppercase tracking-[0.1em] text-midnight-teal underline decoration-midnight-teal/30 underline-offset-4 transition-colors hover:decoration-midnight-teal"
    >
      {label}
    </a>
  );
}

/** Inline text link to another page, matching the site's underline treatment. */
function InlineLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="underline decoration-pine/40 underline-offset-4 transition-colors hover:text-midnight-teal hover:decoration-midnight-teal"
    >
      {children}
    </Link>
  );
}
